import { Formik } from 'formik';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { gql, useQuery } from 'urql';
import { PromptEffect } from '~/components/Prompt';
import { AddressInputGroup } from '~/components/form/AddressInputGroup';
import { StatusToggle } from '~/components/form/StatusToggle';
import { SubmitButton } from '~/components/form/SubmitButton';
import { TextField } from '~/components/form/TextField';

import { ErrorMessage } from '~/components/ui/Error';
import { Loading } from '~/components/ui/Loading';
import { SingleUploadField, UploadResult } from '~/components/upload2/Upload';
import {
  ContactInput,
  ContactType,
  Status,
  useCreateContactMutation,
  useUpdateContactMutation,
} from '~/generated/graphql';
import { graphql } from '~/gql';
import { parseJson } from '~/helpers';
import {
  buildRelToLabel,
  labelToRelationships,
  uniqueSorted,
} from '~/helpers/contactTypeMappings';
import { convertLegacyImagesToUploadResults } from '~/helpers/imageConversion';
import { SideLayout } from '~/layouts/side/SideLayout';
import { useContactListContext } from '../ContactListContext';
import { ContactQuery } from '../ContactView';
import { ContactTypeField } from './ContactTypeField';
import { ContactTypesField } from './ContactTypesField';
import { OrganisationField } from './OrganisationField';

// Contact Type Mappings: label -> relationships[]
const CONTACT_TYPE_MAPPINGS_Q = graphql(`
  query ContactTypeMappingsForForm {
    contactTypeMappings {
      label
      relationships
    }
  }
`);

// Contact Types sources to align options with tenant config
const CONTACT_TYPE_CONFIG_Q = graphql(`
  query ContactTypeConfig_ForForm {
    contactTypeConfig {
      types
      orderMode
      navTypes
      showAllInNav
      isCustomized
      updatedAt
    }
  }
`);
const CONTACT_TYPES_PRESET_Q = graphql(`
  query ContactTypesPreset_ForForm($preset: Boolean) {
    contactTypes(preset: $preset)
  }
`);
const CONTACT_TYPES_IN_USE_Q = graphql(`
  query ContactTypesInUse_ForForm {
    contactTypesInUse
  }
`);
const ALL_CONTACTS_FOR_TYPES_Q = gql`
  query AllContactsForTypes_Form {
    contacts(status: [Active, Inactive]) {
      id
      relationship
    }
  }
`;

export type FormData = {
  status: Status;
  type: ContactType;
  name: string;
  organisationId: string[];
  // UI shows "types" but backend stores relationships
  types: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  streetAddress: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  postalAddress: any;
  email: string;
  phone: string;
  images: UploadResult[];
  notes: string;
};

type Props = {
  title: string;
};

export const ContactForm = ({ title }: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { contactId } = useParams();
  const { refresh } = useContactListContext();

  // TODO: figure out permissions/ pricing
  // Removed condition for ability to manage account while we work on permissions and authorization.
  // const { ability } = useAppContext();
  // const canManageAccount = Boolean(ability?.can?.('manage', 'Account'));

  const [result] = useQuery({
    query: ContactQuery,
    variables: { id: contactId! },
    pause: !contactId,
  });
  const [
    { data: mappingsData, fetching: mappingsFetching, error: mappingsError },
  ] = useQuery({
    query: CONTACT_TYPE_MAPPINGS_Q,
  });
  const [{ data: cfgData, error: cfgError }] = useQuery({
    query: CONTACT_TYPE_CONFIG_Q,
    requestPolicy: 'cache-and-network',
  });
  const [{ data: presetData, fetching: presetFetching, error: presetError }] =
    useQuery({
      query: CONTACT_TYPES_PRESET_Q,
      variables: { preset: true },
    });
  // Defer in-use types query until config and preset are available to avoid early server errors
  const shouldQueryInUse = Boolean(cfgData && presetData);
  const [{ data: inUseData, error: inUseError }] = useQuery({
    query: CONTACT_TYPES_IN_USE_Q,
    pause: !shouldQueryInUse,
    requestPolicy: 'cache-and-network',
  });
  // Fetch all contacts to derive types in use directly from data (fallback for old accounts)
  const [{ data: allContactsData }] = useQuery({
    query: ALL_CONTACTS_FOR_TYPES_Q,
    requestPolicy: 'cache-and-network',
  });
  const mappings: { label: string; relationships: string[] }[] = useMemo(
    () => mappingsData?.contactTypeMappings ?? [],
    [mappingsData?.contactTypeMappings]
  );

  // Derive types in use from actual contact data (fallback for old accounts without config)
  const typesInUseFromContacts = useMemo(() => {
    const contacts = allContactsData?.contacts ?? [];

    const allRelationships: string[] = [];
    contacts.forEach((c: { relationship?: string[] | null }) => {
      const rels = c.relationship ?? [];
      allRelationships.push(...rels.filter((r): r is string => !!r));
    });
    const result = uniqueSorted(
      allRelationships.filter((t) => t.toLowerCase() !== 'other')
    );

    return result;
  }, [allContactsData]);

  // Build base relationship list similar to ManageContactTypes
  const serverTypes: string[] = useMemo(
    () => cfgData?.contactTypeConfig?.types ?? [],
    [cfgData]
  );
  const relPreset: string[] = useMemo(
    () => presetData?.contactTypes ?? [],
    [presetData]
  );
  const relInUse: string[] = useMemo(() => {
    // Combine server-provided list with types computed from actual contact data
    const serverProvided = (inUseData?.contactTypesInUse ?? []).filter(
      (v: string) => !!v
    );

    const result = uniqueSorted([...serverProvided, ...typesInUseFromContacts]);
    return result;
  }, [inUseData, typesInUseFromContacts]);
  const knownRelationships = useMemo(() => {
    const result = uniqueSorted([...(relPreset || []), ...(relInUse || [])]);
    return result;
  }, [relPreset, relInUse]);
  const baseRelationships = useMemo(() => {
    if (serverTypes.length) {
      const extrasInUse = knownRelationships.filter(
        (t) => !serverTypes.includes(t)
      );
      const result = [...serverTypes, ...uniqueSorted(extrasInUse)];
      return result;
    }
    return knownRelationships;
  }, [serverTypes, knownRelationships]);

  const contact = result.data?.contact;
  const [, create] = useCreateContactMutation();
  const [, update] = useUpdateContactMutation();

  // Avoid returning early before hooks; at this point all hooks above have run

  // Avoid early return before hooks; readiness checks will occur after hooks

  // Build reverse index relationship -> label (first label wins)
  const relToLabel = buildRelToLabel(mappings);

  const initialTypes: string[] = useMemo(() => {
    const rels = (contact?.relationship as string[]) ?? [];
    const labels = new Set<string>();
    for (const r of rels) {
      const label = relToLabel.get(String(r)) ?? String(r);
      labels.add(label);
    }
    return Array.from(labels);
  }, [contact?.relationship, relToLabel]);

  const initialValues: FormData = useMemo(
    () => ({
      status: contact?.status ?? Status.Active,
      type: contact?.type ?? ContactType.Person,
      name: contact?.name ?? '',
      organisationId: contact?.organisation ? [contact?.organisation.id] : [],
      types: initialTypes,
      streetAddress: contact?.streetAddress
        ? parseJson(contact.streetAddress)
        : null,
      postalAddress: contact?.postalAddress
        ? parseJson(contact.postalAddress)
        : null,
      email: contact?.email ?? '',
      phone: contact?.phone ?? '',
      images: convertLegacyImagesToUploadResults({
        primaryImageUrl: contact?.image,
        imageIds: contact?.images ?? [],
        fallbackName: 'image.jpg',
        maxResults: 1,
      }),
      notes: contact?.notes ?? '',
    }),
    [contact, initialTypes]
  );

  const handleCancel = () => {
    navigate(contactId ? `/contacts/${contactId}` : '/contacts');
  };

  const labelToRels = (labels: string[]) =>
    labelToRelationships(labels, mappings);

  const handleSubmit = async (values: FormData) => {
    const input: ContactInput = {
      status: values.status,
      type: values.type,
      name: values.name,
      email: values.email,
      phone: values.phone,
      notes: values.notes,
      images: values.images.length > 0 ? [values.images[0].id] : [],
      organisationId: values.organisationId[0] ?? undefined,
      relationship:
        values.types.length > 0 ? labelToRels(values.types) : undefined,
      streetAddress: values.streetAddress
        ? JSON.stringify(values.streetAddress)
        : undefined,
      postalAddress: values.postalAddress
        ? JSON.stringify(values.postalAddress)
        : undefined,
    };

    async function doCreate() {
      const { data, error } = await create({ input });
      return { contactData: data?.createContact, error };
    }

    async function doUpdate(id: string) {
      const { data, error } = await update({ id, input });
      return { contactData: data?.updateContact, error };
    }
    const { contactData, error } = contact
      ? await doUpdate(contact.id)
      : await doCreate();

    if (error) {
      throw error;
    }

    if (contactData) {
      if (refresh) refresh();
      navigate(`/contacts/${contactData.id}`);
    }
  };

  // Build options from configured relationships → labels using mappings; translate labels
  const typeOptions = useMemo(() => {
    // relationship -> label lookup from mappings
    const relToLabel = new Map<string, string>();
    for (const m of mappings) {
      for (const r of m.relationships || []) {
        const key = String(r);
        if (!relToLabel.has(key)) relToLabel.set(key, m.label);
      }
    }
    // Helper to translate known labels via contactRelationships.*
    const translate = (label: string) => {
      const key = `contactRelationships.${label}`;
      const translated = t(key);
      return translated === key ? label : translated;
    };
    // Map ordered relationships to labels, dedupe while preserving order
    const labelsOrdered: string[] = [];
    const seen = new Set<string>();
    for (const rel of baseRelationships) {
      const label = relToLabel.get(String(rel)) ?? String(rel);
      if (!label || seen.has(label)) continue;
      seen.add(label);
      labelsOrdered.push(label);
    }
    const result = labelsOrdered.map((label) => ({
      value: label,
      label: translate(label),
    }));

    return result;
  }, [mappings, baseRelationships, t]);

  // Ensure contact (when editing) AND all metadata are loaded before rendering Formik
  const isEditing = Boolean(contactId);
  const contactError = result.error;
  // Note: inUseError and cfgError are intentionally excluded - if these queries fail,
  // we can still use preset types. The inUse and config data are only for enhancing
  // the type list. This allows non-owner users to edit contacts using preset types.
  const firstError = contactError || mappingsError || presetError;
  const isMetadataReady = Boolean(mappingsData && presetData);

  // Log non-blocking errors once for debugging but don't block the form
  const inUseWarnedRef = useRef(false);
  const cfgWarnedRef = useRef(false);
  useEffect(() => {
    if (inUseError && !inUseWarnedRef.current) {
      console.warn(
        'ContactForm: contactTypesInUse query failed, continuing with preset types only',
        inUseError
      );
      inUseWarnedRef.current = true;
    }
    if (cfgError && !cfgWarnedRef.current) {
      console.warn(
        'ContactForm: contactTypeConfig query failed (likely permissions), continuing with preset types only',
        cfgError
      );
      cfgWarnedRef.current = true;
    }
  }, [inUseError, cfgError]);

  if (firstError) {
    // Basic telemetry/logging; replace with real telemetry as available

    console.error('ContactForm query error', {
      contactError,
      mappingsError,
      cfgError,
      presetError,
    });
    return (
      <ErrorMessage
        message={firstError?.message || t('unknownError')}
        onBack={handleCancel}
      />
    );
  }

  // Note: inUseFetching and cfgFetching intentionally excluded - don't wait for them
  // as they may fail due to permissions but the form can work without them
  const isFetchingMetadata = mappingsFetching || presetFetching;

  if ((isEditing && !contact) || isFetchingMetadata || !isMetadataReady) {
    return <Loading />;
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      <SideLayout>
        <PromptEffect />
        <SideLayout.Head onClose={handleCancel}>{title}</SideLayout.Head>
        <SideLayout.Body>
          <StatusToggle />
          <ContactTypeField />
          <TextField className='mb-5 pt-2' name='name' label={t('name')} />
          <OrganisationField />
          <ContactTypesField options={typeOptions} />
          <TextField name='phone' label={t('phoneNumber')} type='phone' />
          <TextField name='email' label={t('emailAddress')} type='email' />
          <AddressInputGroup name='streetAddress' label={t('streetAddress')} />
          <AddressInputGroup name='postalAddress' label={t('postalAddress')} />
          <TextField name='notes' label={t('notes')} type='textarea' />
          <SingleUploadField
            name='images'
            id='contact-image'
            imagesOnly
            isMainUpload
          />
        </SideLayout.Body>
        <SideLayout.Foot className='p-4'>
          <SubmitButton />
        </SideLayout.Foot>
      </SideLayout>
    </Formik>
  );
};
